import { SubDomain } from '@core/enums/sub-domain.enum';
import { Theme } from '@core/enums/theme.enum';
import { ISiteConfig } from '@core/interfaces/site-config.interface';
import { environment } from '@env';

// DEFAULT

const DEFAULT_SITE_CONFIG: ISiteConfig = {
  theme: Theme.DEFAULT,
  platformLabel: ['Inter', 'Ticket'],
  termsAndConditionsSrc: 'assets/terms/da.html',
  privacyPolicySrc: 'assets/privacy/da.html',
  showVenueMap: true,
  showServicesAgreementInFooter: false, // Link to billetexpressen.net
};

// DANISH

const DANISH_SITE_CONFIG: ISiteConfig = {
  platformLabel: ['Billet', 'Expressen'],
  termsAndConditionsSrc: 'assets/terms/da.html',
  privacyPolicySrc: 'assets/privacy/da.html',
  showServicesAgreementInFooter: true,
};

const OPLEVBRONDBY_SITE_CONFIG: ISiteConfig = {
  ...DANISH_SITE_CONFIG,
  theme: Theme.OPLEVBRONDBY,
  logoSrc: 'assets/logos/oplevbrondby.png',
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.oplevbrondby },
  },
};

const AARHUSBRYGHUS_SITE_CONFIG: ISiteConfig = {
  ...DANISH_SITE_CONFIG,
  theme: Theme.AARHUSBRYGHUS,
  logoSrc: 'assets/logos/aarhusbryghus.png',
};

const BEGHUSET_SITE_CONFIG: ISiteConfig = {
  ...DANISH_SITE_CONFIG,
  theme: Theme.BEGHUSET,
  logoSrc: 'assets/logos/beghuset.png',
};

// POLISH

const ONLINE_INTERTICKET_PL_CONFIG: ISiteConfig = {
  theme: Theme.ORANGE,
  platformLabel: ['', ''],
  logoSrc: 'assets/logos/onlinepl.png',
  termsAndConditionsSrc: 'assets/terms/pl.html',
  privacyPolicySrc: 'assets/privacy/pl.html',
  showVenueMap: false,
};

const TEATR_MALY_SITE_CONFIG: ISiteConfig = {
  theme: Theme.TEATR_MALY,
  logoSrc: 'assets/logos/teatrmaly.jpg',
  termsAndConditionsSrc: 'assets/terms/pl.html',
  privacyPolicySrc: 'assets/privacy/pl.html',
  showVenueMap: false,
};

const MEDITACJE_PL_SITE_CONFIG: ISiteConfig = {
  ...ONLINE_INTERTICKET_PL_CONFIG,
  platformLabel: ['Medytacje', ''],
};

// HUNGARIAN

const HUNGARIAN_SITE_CONFIG: ISiteConfig = {
  theme: Theme.ORANGE,
  logoSrc: 'assets/hungarian/jegyhu.png',
  showVenueMap: false,
  termsLink: 'https://www.jegy.hu/aszf',
  platformLabel: ['', ''],
};

const TSU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Tudományos Stand Up', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.tudomanyosstandup },
  },
};

const MALADYPE_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Maladype Színház', ''],
};

const KODALYFILHARMONIA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Kodály Filharmónia', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.kodalyfilharmonia },
  },
};

const PFZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Pannon Filharmonikusok', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.pfz },
  },
};

const ODZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Óbudai Danubia Zenekar', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.obidaidanubia },
  },
};

const FOVAROSI_NAGYCIRKUSZ_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Fővárosi Nagycirkusz', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.fovarosinagycirkus },
  },
};

const BDZ_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budafoki Dohnányi Zenekar', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.budafokidohnanyi },
  },
};

const ESERNYOS_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Esernyős Kulturális Tér', ''],
};

const GITARFESZT_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budapesti Nemzetközi Gitárfesztivál', ''],
};

const BUDAVAR_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budavári Önkormányzat', ''],
};

const VARKERT_BAZAR_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Várkert Bazár', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.varketbazar },
  },
};

const MILLENARIS_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Millenáris', ''],
};

const HAGYOMANYOK_HAZA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Hagyományok Háza', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.hagyomanyokhaza },
  },
};

const ZENEACADEMY_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Online koncertek a Zeneakadémián', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.lisztacademy },
  },
};

const ATRIUM_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Átrium online', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.atrium },
  },
};

const HARANGROZSA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Hangrózsa', ''],
};

const PANNONVARSZINHAZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Pannon Várszínház', ''],
};

const RENDBENVAN_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Rendben Van Kft Pódiumbeszélgetések', ''],
};

const KOLIBRI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Kolibri Színház', ''],
};

const JMMH_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budaörsi Jókai Mór Művelődési Ház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.budaorsijmmh },
  },
};

const PESTI_MAGYAR_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Pesti Magyar Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.pestimagyarszinhaz },
  },
};

const TINTANYUL_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Farkasházi Réka és a Tintanyúl', ''],
};

const SPINOZA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Spinoza Színház', ''],
};

const FONIX_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Főnix Rendezvényszervező', ''],
};

const AZONGORA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['MVM Koncertek - A Zongora', ''],
};

const FABULA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Fabula Bábszínház', ''],
};

const BABSZINHAZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budapest Bábszínház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.budapestbabszinhaz },
  },
};

const BETHLEN_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Bethlen Téri Színház', ''],
};

const SZEGEDI_SZABADTERI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Szegedi Szabadtéri Játékok - REÖK', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.szegediszabadteri },
  },
};

const SZENTENDRE_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Szentendrei Kulturális Központ - Kult Online', ''],
};

const MAV_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Máv Szimfonikus Zenekar', ''],
};

const SPIRIT_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Spirit Színház', ''],
};

const CSIK_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Csík Zenekar', ''],
};

const ZICHY_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Zichy Szín-Műhely', ''],
};

const GYORI_NEMZETI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Győri Nemzeti Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.gyorinemzeti },
  },
};

const NEFELEJCS_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Nefelejcs Bábszínház', ''],
};

const SZOLNOKI_SZIGLIGETI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Szolnoki Szigligeti Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.szigligetiszinhaz },
  },
};

const B32_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['B32 Galéria és Kultúrtér', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.b32 },
  },
};

const PINTER_BELA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Pintér Béla és Társulata', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.pbest },
  },
};

const GOZON_GYULA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Gózon Gyula Kamaraszínház', ''],
};

const BOGGIE_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Boggie Production', ''],
};

const BEKESCSABAI_JOKAI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Békéscsabai Jókai Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.bekescsabaijokai },
  },
};

const LATHATARON_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Láthatáron Csoport', ''],
};

const CIROKA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Ciróka Bábszínház', ''],
};

const OPERETT_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budapesti Operettszínház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.operett },
  },
};

const BARTOK_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Bartók Kamaraszínház', ''],
};

const PODCAST_SHOW_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Ezmi Visual Podcast Show', ''],
};

const ROXINHAZ_SHOW_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Roxínház - Kaposvár', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.roxinhaz },
  },
};

const LASZLO_ATTILA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['László Attila és zenekara', ''],
};

const VERES_1_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Veres 1 Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.veres1szinhaz },
  },
};

const CSANYI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Csányi Sándor Produkció', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.csanyisandorprodukcio },
  },
};

const KARAVAN_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Karaván Színház', ''],
};

const URANIA_NEMZETI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Uránia Nemzeti Filmszínház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.uranianf },
  },
};

const MISKOLCI_VSZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Miskolc Városi Szabadidőközpont', ''],
};

const JASZAI_MARI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Jászai Mari Színház · Népház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.jaszaimari },
  },
};

const TRAFO_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Trafó', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.trafo },
  },
};

const TULIPANTUNDER_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['TulipánTündér', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.tulipantunder },
  },
};

const RAKASZ_GERGELY_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Rákász Gergely - orgonaművész', ''],
};

const PINCE_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Ferencvárosi Pinceszínház', ''],
};

const NAPILAPCSOPORT_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Napilapcsoport TV', ''],
};

const GOLEMSZINHAZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Gólem Központ', ''],
};

const MERRYGOROUND_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Merry Go Round', ''],
};

const DUMASZINHAZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Dumaszínház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.dumaszinhaz },
  },
};

const KULTURKOZPONT_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Óbudai Kulturális Központ', ''],
};

const BUDAPESTKLEZMERBAND_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budapest Klezmer Band', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.klezmer },
  },
};

const TOBEART_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['TOBE ART Productions', ''],
};

const NEWU_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['NewU Művészmenedzsment Ügynökség', ''],
};

const SASIPRODUKCIO_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Sasi Produkció', ''],
};

const KAPOSMUSICAL_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['KaposMusical - HOME', ''],
};

const MAO_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Modern Art Orchestra', ''],
};

const OPERA_OTTHONRA_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Magyar Állami Operaház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.operaotthonra },
  },
};

const PANNONIA_ENTERTAINMENT_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Pannonia Entertainment - Opera / Balett / Színház / Koncert közvetítések', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.pannoniaentertainment },
  },
};

const BUDAPEST_JAZZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Budapest Jazz Club', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.budapestjazz },
  },
};

const HATSZIN_TEATRUM_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Hatszín Teátrum', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.hatszin },
  },
};

const GYORI_BALETT_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Győri Balett', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.gyoribalett },
  },
};

const VIRTUOZOK_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Virtuózok V4+', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.virtuozok },
  },
};

const FONO_BUDAI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Fonó Budai Zeneház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.fonobudaizenekar },
  },
};

const VOROSMARTY_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Vörösmarty Színház', ''],
};

const QUIMBY_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Quimby Plusz-Mínusz koncertfilm', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.quimby },
  },
};

const BAGOSSY_BROTHERS_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Bagossy Brothers Company - Fordul a világ - lemezbemutató koncertfilm', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.bagossy },
  },
};

const NEMZETI_TANCSZINHAZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Nemzeti Táncszínház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.nemzetitancszinhaz },
  },
};

const SZEGEDI_NEMZETI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Szegedi Nemzeti Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.szegedinemzeti },
  },
};

const VIGSZINHAZ_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Vígszínház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.vigszinhaz },
  },
};

const MOZGO_MOZI_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Mozgó Mozi', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.mozgomozi },
  },
};

const DUNA_TANCMUHELY_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Duna Táncműhely', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.dunatancmuhely },
  },
};

const SZKENE_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Szkéné Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.szkene },
  },
};

const PINDROCH_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Pindroch Csaba Onlinekomédia', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.pindroch },
  },
};

const SOLTIS_LAJON_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Soltis Lajos Színház', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.soltis },
  },
};

const CSANYI_ZART_JEGY_HU_SITE_CONFIG: ISiteConfig = {
  ...HUNGARIAN_SITE_CONFIG,
  platformLabel: ['Csányi Sándor Produkció - Zárt', ''],
  scriptLoaderConfig: {
    googleAnalytics: { apiKey: environment.google.analytics.csanyisandorprodukciozart },
  },
};

// MALTESE
const ONLINE_MFA_SITE_CONFIG: ISiteConfig = {
  theme: Theme.MFA,
  logoSrc: 'assets/logos/mfa.png',
  termsAndConditionsSrc: 'assets/terms/mt.html',
  privacyPolicySrc: 'assets/privacy/mt.html',
  showVenueMap: false,
};

// USA
const EVAALEXIS_SITE_CONFIG: ISiteConfig = {
  theme: Theme.EVAALEXIS,
  logoSrc: 'assets/logos/evaalexis.jpg',
  termsLink: 'https://www.jegy.hu/articles/625/az-interticket-kft-altalanos-szerzodesi-feltetelei?lang=en',
  privacyLink: 'https://www.jegy.hu/articles/655/privacy-policy?lang=en',
  showVenueMap: true,
  platformLabel: ['', ''],
  platformLabelImage: true,
};

export const SITE_CONFIG: { [key in SubDomain]?: ISiteConfig } = {
  [SubDomain.DEFAULT]: DEFAULT_SITE_CONFIG,
  [SubDomain.OPLEVBRONDBY]: OPLEVBRONDBY_SITE_CONFIG,
  [SubDomain.AARHUSBRYGHUS]: AARHUSBRYGHUS_SITE_CONFIG,
  [SubDomain.BEGHUSET]: BEGHUSET_SITE_CONFIG,
  [SubDomain.ONLINE_INTERTICKET_PL]: ONLINE_INTERTICKET_PL_CONFIG,
  [SubDomain.TEATR_MALY]: TEATR_MALY_SITE_CONFIG,
  [SubDomain.MEDITACJE_PL]: MEDITACJE_PL_SITE_CONFIG,
  [SubDomain.TSU_JEGY_HU]: TSU_SITE_CONFIG,
  [SubDomain.MALADYPE_JEGY_HU]: MALADYPE_JEGY_HU_SITE_CONFIG,
  [SubDomain.KODALYFILHARMONIA_JEGY_HU]: KODALYFILHARMONIA_JEGY_HU_SITE_CONFIG,
  [SubDomain.PFZ_JEGY_HU]: PFZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.ODZ_JEGY_HU]: ODZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.ONLINE_JEGY_HU]: HUNGARIAN_SITE_CONFIG,
  [SubDomain.FOVAROSI_NAGYCIRKUSZ]: FOVAROSI_NAGYCIRKUSZ_SITE_CONFIG,
  [SubDomain.ONLINE_MFA]: ONLINE_MFA_SITE_CONFIG,
  [SubDomain.ESERNYOS_JEGY_HU]: ESERNYOS_JEGY_HU_SITE_CONFIG,
  [SubDomain.EVAALEXIS]: EVAALEXIS_SITE_CONFIG,
  [SubDomain.BDZ_JEGY_HU]: BDZ_SITE_CONFIG,
  [SubDomain.GITARFESZT_JEGY_HU]: GITARFESZT_JEGY_HU_SITE_CONFIG,
  [SubDomain.BUDAVAR_JEGY_HU]: BUDAVAR_JEGY_HU_SITE_CONFIG,
  [SubDomain.MILLENARIS_JEGY_HU]: MILLENARIS_JEGY_HU_SITE_CONFIG,
  [SubDomain.HAGYOMANYOK_HAZA_JEGY_HU]: HAGYOMANYOK_HAZA_JEGY_HU_SITE_CONFIG,
  [SubDomain.ZENEACADEMY_JEGY_HU]: ZENEACADEMY_JEGY_HU_SITE_CONFIG,
  [SubDomain.ATRIUM_JEGY_HU]: ATRIUM_JEGY_HU_SITE_CONFIG,
  [SubDomain.HARANGROZSA_JEGY_HU]: HARANGROZSA_JEGY_HU_SITE_CONFIG,
  [SubDomain.PANNONVARSZINHAZ_JEGY_HU]: PANNONVARSZINHAZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.VARKERT_BAZAR_JEGY_HU]: VARKERT_BAZAR_JEGY_HU_SITE_CONFIG,
  [SubDomain.RENDBENVAN_JEGY_HU]: RENDBENVAN_JEGY_HU_SITE_CONFIG,
  [SubDomain.KOLIBRISZINHAZ_JEGY_HU]: KOLIBRI_JEGY_HU_SITE_CONFIG,
  [SubDomain.JMMH_JEGY_HU]: JMMH_JEGY_HU_SITE_CONFIG,
  [SubDomain.PESTI_MAGYAR_SZINHAZ_JEGY_HU]: PESTI_MAGYAR_JEGY_HU_SITE_CONFIG,
  [SubDomain.TINTANYUL_JEGY_HU]: TINTANYUL_JEGY_HU_SITE_CONFIG,
  [SubDomain.SPINOZA_JEGY_HU]: SPINOZA_JEGY_HU_SITE_CONFIG,
  [SubDomain.AZONGORA_JEGY_HU]: AZONGORA_JEGY_HU_SITE_CONFIG,
  [SubDomain.FONIX_JEGY_HU]: FONIX_JEGY_HU_SITE_CONFIG,
  [SubDomain.FABULA_JEGY_HU]: FABULA_JEGY_HU_SITE_CONFIG,
  [SubDomain.BABSZINHAZ_JEGY_HU]: BABSZINHAZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.BETHLEN_JEGY_HU]: BETHLEN_JEGY_HU_SITE_CONFIG,
  [SubDomain.SZEGED_SZABADTERI_JEGY_HU]: SZEGEDI_SZABADTERI_JEGY_HU_SITE_CONFIG,
  [SubDomain.SZENTENDRE_JEGY_HU]: SZENTENDRE_JEGY_HU_SITE_CONFIG,
  [SubDomain.MAV_JEGY_HU]: MAV_JEGY_HU_SITE_CONFIG,
  [SubDomain.SPIRIT_JEGY_HU]: SPIRIT_JEGY_HU_SITE_CONFIG,
  [SubDomain.CSIK_JEGY_HU]: CSIK_JEGY_HU_SITE_CONFIG,
  [SubDomain.ZICHY_JEGY_HU]: ZICHY_JEGY_HU_SITE_CONFIG,
  [SubDomain.GYORI_NEMZETI_JEGY_HU]: GYORI_NEMZETI_JEGY_HU_SITE_CONFIG,
  [SubDomain.NEFELEJCS_JEGY_HU]: NEFELEJCS_JEGY_HU_SITE_CONFIG,
  [SubDomain.SZOLNOKI_SZIGLIGETI_JEGY_HU]: SZOLNOKI_SZIGLIGETI_JEGY_HU_SITE_CONFIG,
  [SubDomain.B32_JEGY_HU]: B32_JEGY_HU_SITE_CONFIG,
  [SubDomain.PINTER_BELA_JEGY_HU]: PINTER_BELA_JEGY_HU_SITE_CONFIG,
  [SubDomain.GOZON_JEGY_HU]: GOZON_GYULA_JEGY_HU_SITE_CONFIG,
  [SubDomain.BOGGIE_JEGY_HU]: BOGGIE_JEGY_HU_SITE_CONFIG,
  [SubDomain.BEKESCSABAI_JOKAI_JEGY_HU]: BEKESCSABAI_JOKAI_JEGY_HU_SITE_CONFIG,
  [SubDomain.LATHATARON_JEGY_HU]: LATHATARON_JEGY_HU_SITE_CONFIG,
  [SubDomain.CIROKA_JEGY_HU]: CIROKA_JEGY_HU_SITE_CONFIG,
  [SubDomain.OPERETT_JEGY_HU]: OPERETT_JEGY_HU_SITE_CONFIG,
  [SubDomain.BARTOK_JEGY_HU]: BARTOK_JEGY_HU_SITE_CONFIG,
  [SubDomain.PODCAST_SHOW_JEGY_HU]: PODCAST_SHOW_JEGY_HU_SITE_CONFIG,
  [SubDomain.ROXINHAZ_JEGY_HU]: ROXINHAZ_SHOW_JEGY_HU_SITE_CONFIG,
  [SubDomain.LASZLO_ATTILA_JEGY_HU]: LASZLO_ATTILA_JEGY_HU_SITE_CONFIG,
  [SubDomain.VERES_1_JEGY_HU]: VERES_1_JEGY_HU_SITE_CONFIG,
  [SubDomain.CSANYI_JEGY_HU]: CSANYI_JEGY_HU_SITE_CONFIG,
  [SubDomain.KARAVAN_JEGY_HU]: KARAVAN_JEGY_HU_SITE_CONFIG,
  [SubDomain.URANIA_NEMZETI_JEGY_HU]: URANIA_NEMZETI_JEGY_HU_SITE_CONFIG,
  [SubDomain.MISKOLCI_VSZ_JEGY_HU]: MISKOLCI_VSZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.JASZAI_MARI_JEGY_HU]: JASZAI_MARI_JEGY_HU_SITE_CONFIG,
  [SubDomain.TRAFO_JEGY_HU]: TRAFO_JEGY_HU_SITE_CONFIG,
  [SubDomain.TULIPANTUNDER_JEGY_HU]: TULIPANTUNDER_JEGY_HU_SITE_CONFIG,
  [SubDomain.RAKASZ_GERGELY_JEGY_HU]: RAKASZ_GERGELY_JEGY_HU_SITE_CONFIG,
  [SubDomain.PINCE_JEGY_HU]: PINCE_JEGY_HU_SITE_CONFIG,
  [SubDomain.NAPILAPCSOPORT_JEGY_HU]: NAPILAPCSOPORT_JEGY_HU_SITE_CONFIG,
  [SubDomain.GOLEMSZINHAZ_JEGY_HU]: GOLEMSZINHAZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.MERRYGOROUND_JEGY_HU]: MERRYGOROUND_JEGY_HU_SITE_CONFIG,
  [SubDomain.DUMASZINHAZ_JEGY_HU]: DUMASZINHAZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.KULTURKOZPONT_JEGY_HU]: KULTURKOZPONT_JEGY_HU_SITE_CONFIG,
  [SubDomain.BUDAPESTKLEZMERBAND_JEGY_HU]: BUDAPESTKLEZMERBAND_JEGY_HU_SITE_CONFIG,
  [SubDomain.TOBEART_JEGY_HU]: TOBEART_JEGY_HU_SITE_CONFIG,
  [SubDomain.NEWU_JEGY_HU]: NEWU_JEGY_HU_SITE_CONFIG,
  [SubDomain.SASIPRODUKCIO_JEGY_HU]: SASIPRODUKCIO_JEGY_HU_SITE_CONFIG,
  [SubDomain.KAPOSMUSICAL_JEGY_HU]: KAPOSMUSICAL_JEGY_HU_SITE_CONFIG,
  [SubDomain.MAO_JEGY_HU]: MAO_JEGY_HU_SITE_CONFIG,
  [SubDomain.OPERA_OTTHONRA_JEGY_HU]: OPERA_OTTHONRA_JEGY_HU_SITE_CONFIG,
  [SubDomain.PANNONIA_ENTERTAINMENT_JEGY_HU]: PANNONIA_ENTERTAINMENT_JEGY_HU_SITE_CONFIG,
  [SubDomain.BUDAPEST_JAZZ_JEGY_HU]: BUDAPEST_JAZZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.HATSZIN_TEATRUM_JEGY_HU]: HATSZIN_TEATRUM_JEGY_HU_SITE_CONFIG,
  [SubDomain.GYORI_BALETT_JEGY_HU]: GYORI_BALETT_JEGY_HU_SITE_CONFIG,
  [SubDomain.VIRTUOZOK_JEGY_HU]: VIRTUOZOK_JEGY_HU_SITE_CONFIG,
  [SubDomain.FONO_BUDAI_JEGY_HU]: FONO_BUDAI_JEGY_HU_SITE_CONFIG,
  [SubDomain.VOROSMARTY_JEGY_HU]: VOROSMARTY_JEGY_HU_SITE_CONFIG,
  [SubDomain.QUIMBY_JEGY_HU]: QUIMBY_JEGY_HU_SITE_CONFIG,
  [SubDomain.BAGOSSY_BROTHERS_JEGY_HU]: BAGOSSY_BROTHERS_JEGY_HU_SITE_CONFIG,
  [SubDomain.NEMZETI_TANCSZINHAZ_JEGY_HU]: NEMZETI_TANCSZINHAZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.SZEGEDI_NEMZETI_JEGY_HU]: SZEGEDI_NEMZETI_JEGY_HU_SITE_CONFIG,
  [SubDomain.VIGSZINHAZ_JEGY_HU]: VIGSZINHAZ_JEGY_HU_SITE_CONFIG,
  [SubDomain.MOZGO_MOZI_JEGY_HU]: MOZGO_MOZI_JEGY_HU_SITE_CONFIG,
  [SubDomain.DUNA_TANCMUHELY_JEGY_HU]: DUNA_TANCMUHELY_JEGY_HU_SITE_CONFIG,
  [SubDomain.SZKENE_JEGY_HU]: SZKENE_JEGY_HU_SITE_CONFIG,
  [SubDomain.PINDROCH_JEGY_HU]: PINDROCH_JEGY_HU_SITE_CONFIG,
  [SubDomain.SOLTIS_LAJON_JEGY_HU]: SOLTIS_LAJON_JEGY_HU_SITE_CONFIG,
  [SubDomain.CSANYI_ZART_JEGY_HU]: CSANYI_ZART_JEGY_HU_SITE_CONFIG,
};
